import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Plus, FileSignature, Edit, Trash2, Eye, Users, Settings, Loader2, MoreHorizontal, FileText, Upload, Link, PenSquare } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TemplateEditorDialog } from "@/components/template-editor-dialog";
import type { DocumentTemplate, DeliveryMethod, TemplateStatus, DocumentTemplateType } from "@shared/schema";

interface FormsTemplatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

export function FormsTemplatesDialog({ open, onOpenChange, inPanel = false }: FormsTemplatesDialogProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("templates");
  const [editingTemplate, setEditingTemplate] = useState<DocumentTemplate | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [configuringRolesTemplate, setConfiguringRolesTemplate] = useState<DocumentTemplate | null>(null);
  const [configuringFieldsTemplate, setConfiguringFieldsTemplate] = useState<DocumentTemplate | null>(null);
  const [uploadingTemplate, setUploadingTemplate] = useState<DocumentTemplate | null>(null);
  const [editorTemplate, setEditorTemplate] = useState<DocumentTemplate | null>(null);
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    templateType: "contract" as DocumentTemplateType,
    deliveryMethod: "internal" as DeliveryMethod,
    status: "draft" as TemplateStatus,
  });

  const { data: templates = [], isLoading } = useQuery<DocumentTemplate[]>({
    queryKey: ["/api/document-templates"],
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; templateType: DocumentTemplateType; deliveryMethod: DeliveryMethod; status: TemplateStatus }) => {
      return apiRequest("POST", "/api/document-templates", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/document-templates"] });
      setShowCreateForm(false);
      setNewTemplate({ name: "", templateType: "contract", deliveryMethod: "internal", status: "draft" });
      toast({ title: "Template created", description: "Your new template has been created" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<{ name: string; templateType: DocumentTemplateType; deliveryMethod: DeliveryMethod; status: TemplateStatus }> }) => {
      return apiRequest("PATCH", `/api/document-templates/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/document-templates"] });
      setEditingTemplate(null);
      toast({ title: "Template updated", description: "Your template has been updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/document-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/document-templates"] });
      toast({ title: "Template deleted", description: "Your template has been deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft":
        return <Badge variant="secondary" data-testid="badge-status-draft">Draft</Badge>;
      case "published":
        return <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" data-testid="badge-status-published">Published</Badge>;
      case "archived":
        return <Badge variant="outline" className="text-muted-foreground" data-testid="badge-status-archived">Archived</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getDeliveryButton = (template: DocumentTemplate) => {
    if (template.deliveryMethod === "internal") {
      return (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setUploadingTemplate(template)}
          data-testid={`button-upload-${template.id}`}
        >
          <Upload className="w-3 h-3 mr-1" />
          Upload
        </Button>
      );
    } else if (template.deliveryMethod === "docusign") {
      return (
        <Badge variant="outline" className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" data-testid="badge-delivery-docusign">
          <Link className="w-3 h-3 mr-1" />DocuSign
        </Badge>
      );
    }
    return <Badge variant="outline">{template.deliveryMethod}</Badge>;
  };

  const handleCreate = () => {
    if (!newTemplate.name.trim()) {
      toast({ title: "Name required", description: "Please enter a template name", variant: "destructive" });
      return;
    }
    createMutation.mutate(newTemplate);
  };

  const handleDelete = (id: string) => {
    if (confirm("Are you sure you want to delete this template?")) {
      deleteMutation.mutate(id);
    }
  };

  const innerContent = (<>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5" />
            Forms & Contracts Templates
          </DialogTitle>
          <DialogDescription>
            Create and manage document templates for forms and contracts
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList>
            <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
            <TabsTrigger value="docusign" data-testid="tab-docusign">DocuSign</TabsTrigger>
          </TabsList>

          <TabsContent value="templates" className="flex-1 overflow-hidden mt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-muted-foreground" />
                <span className="font-medium">Document Templates</span>
                <Badge variant="secondary">{templates.length}</Badge>
              </div>
              <Button size="sm" onClick={() => setShowCreateForm(true)} data-testid="button-create-template">
                <Plus className="w-4 h-4 mr-2" />
                New Template
              </Button>
            </div>

            {showCreateForm && (
              <Card className="mb-4">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Create New Template</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="template-name">Name</Label>
                      <Input
                        id="template-name"
                        placeholder="e.g., Performance Contract"
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                        data-testid="input-template-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="template-type">Template Type</Label>
                      <Select
                        value={newTemplate.templateType}
                        onValueChange={(value: DocumentTemplateType) => setNewTemplate(prev => ({ ...prev, templateType: value }))}
                      >
                        <SelectTrigger data-testid="select-template-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="contract">Contract</SelectItem>
                          <SelectItem value="form">Form</SelectItem>
                          <SelectItem value="release">Release</SelectItem>
                          <SelectItem value="rider">Rider</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="template-delivery">Delivery Method</Label>
                      <Select
                        value={newTemplate.deliveryMethod}
                        onValueChange={(value: DeliveryMethod) => setNewTemplate(prev => ({ ...prev, deliveryMethod: value }))}
                      >
                        <SelectTrigger data-testid="select-delivery-method">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="internal">Internal Upload</SelectItem>
                          <SelectItem value="docusign">DocuSign</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowCreateForm(false)} data-testid="button-cancel-create">
                      Cancel
                    </Button>
                    <Button onClick={handleCreate} disabled={createMutation.isPending} data-testid="button-save-template">
                      {createMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Create Template
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <ScrollArea className="h-[calc(85vh-340px)]">
              {isLoading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : templates.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-center">
                  <FileSignature className="w-10 h-10 text-muted-foreground mb-2" />
                  <p className="font-medium">No templates yet</p>
                  <p className="text-sm text-muted-foreground">Create your first template to get started</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Delivery</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templates.map((template: DocumentTemplate) => (
                      <TableRow key={template.id} data-testid={`row-template-${template.id}`}>
                        <TableCell className="font-medium">{template.name}</TableCell>
                        <TableCell>{getStatusBadge(template.status)}</TableCell>
                        <TableCell>{getDeliveryButton(template)}</TableCell>
                        <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                          {template.templateType}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`button-template-actions-${template.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => setEditorTemplate(template)}
                                disabled={!template.fileObjectKey}
                                data-testid={`menu-open-editor-${template.id}`}
                              >
                                <PenSquare className="w-4 h-4 mr-2" />
                                Open Editor
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setEditingTemplate(template)} data-testid={`menu-edit-${template.id}`}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Settings
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setConfiguringRolesTemplate(template)} data-testid={`menu-roles-${template.id}`}>
                                <Users className="w-4 h-4 mr-2" />
                                Configure Roles
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setConfiguringFieldsTemplate(template)} data-testid={`menu-fields-${template.id}`}>
                                <Settings className="w-4 h-4 mr-2" />
                                Configure Fields
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  updateMutation.mutate({
                                    id: template.id,
                                    data: { status: template.status === "published" ? "draft" : "published" },
                                  });
                                }}
                                data-testid={`menu-publish-${template.id}`}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                {template.status === "published" ? "Unpublish" : "Publish"}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleDelete(template.id)}
                                data-testid={`menu-delete-${template.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="docusign" className="flex-1 overflow-hidden mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Link className="w-5 h-5" />
                  DocuSign Integration
                </CardTitle>
                <CardDescription>
                  Connect your DocuSign account to enable electronic signatures
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <FileSignature className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium">DocuSign Account</p>
                      <p className="text-sm text-muted-foreground">Not connected</p>
                    </div>
                  </div>
                  <Button variant="outline" data-testid="button-connect-docusign">
                    Connect DocuSign
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  DocuSign integration allows you to send contracts for electronic signature. 
                  Recipients can sign documents digitally without printing or scanning.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <TemplateEditorDialog
          open={!!editorTemplate}
          onOpenChange={(o) => { if (!o) setEditorTemplate(null); }}
          template={editorTemplate}
        />

        {editingTemplate && (
          <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Template</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name</Label>
                  <Input
                    id="edit-name"
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate(prev => prev ? { ...prev, name: e.target.value } : null)}
                    data-testid="input-edit-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={editingTemplate.status}
                    onValueChange={(value: TemplateStatus) => setEditingTemplate(prev => prev ? { ...prev, status: value } : null)}
                  >
                    <SelectTrigger data-testid="select-edit-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingTemplate(null)} data-testid="button-cancel-edit">
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (editingTemplate) {
                      updateMutation.mutate({
                        id: editingTemplate.id,
                        data: {
                          name: editingTemplate.name,
                          status: editingTemplate.status,
                        },
                      });
                    }
                  }}
                  disabled={updateMutation.isPending}
                  data-testid="button-update-template"
                >
                  {updateMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Configure Roles Dialog */}
        {configuringRolesTemplate && (
          <Dialog open={!!configuringRolesTemplate} onOpenChange={() => setConfiguringRolesTemplate(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Configure Roles - {configuringRolesTemplate.name}
                </DialogTitle>
                <DialogDescription>
                  Define which roles can access and sign this document template
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-3">
                  <Label>Required Signatures</Label>
                  <div className="space-y-2">
                    {["Client", "Performer", "Agent", "Manager", "Witness"].map((role) => (
                      <div key={role} className="flex items-center gap-3 p-2 border rounded">
                        <input type="checkbox" id={`role-${role}`} className="rounded" data-testid={`checkbox-role-${role.toLowerCase()}`} />
                        <Label htmlFor={`role-${role}`} className="font-normal">{role}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfiguringRolesTemplate(null)} data-testid="button-cancel-roles">
                  Cancel
                </Button>
                <Button onClick={() => {
                  toast({ title: "Roles saved", description: "Role configuration has been saved" });
                  setConfiguringRolesTemplate(null);
                }} data-testid="button-save-roles">
                  Save Roles
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Configure Fields Dialog */}
        {configuringFieldsTemplate && (
          <Dialog open={!!configuringFieldsTemplate} onOpenChange={() => setConfiguringFieldsTemplate(null)}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Configure Fields - {configuringFieldsTemplate.name}
                </DialogTitle>
                <DialogDescription>
                  Define the merge fields available in this document template
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-3">
                  <Label>Available Merge Fields</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                    {[
                      { name: "Client Name", field: "{{client_name}}" },
                      { name: "Client Address", field: "{{client_address}}" },
                      { name: "Event Date", field: "{{event_date}}" },
                      { name: "Event Time", field: "{{event_time}}" },
                      { name: "Event Location", field: "{{event_location}}" },
                      { name: "Performer Name", field: "{{performer_name}}" },
                      { name: "Event Rate", field: "{{event_rate}}" },
                      { name: "Deposit Amount", field: "{{deposit_amount}}" },
                      { name: "Balance Due", field: "{{balance_due}}" },
                      { name: "Invoice Number", field: "{{invoice_number}}" },
                    ].map((item) => (
                      <div key={item.field} className="flex items-center gap-2 p-2 border rounded text-sm">
                        <input type="checkbox" id={`field-${item.field}`} className="rounded" defaultChecked data-testid={`checkbox-field-${item.field.replace(/[{}]/g, '')}`} />
                        <div className="flex-1">
                          <Label htmlFor={`field-${item.field}`} className="font-normal text-sm">{item.name}</Label>
                          <p className="text-xs text-muted-foreground font-mono">{item.field}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfiguringFieldsTemplate(null)} data-testid="button-cancel-fields">
                  Cancel
                </Button>
                <Button onClick={() => {
                  toast({ title: "Fields saved", description: "Field configuration has been saved" });
                  setConfiguringFieldsTemplate(null);
                }} data-testid="button-save-fields">
                  Save Fields
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Upload Template Dialog */}
        {uploadingTemplate && (
          <Dialog open={!!uploadingTemplate} onOpenChange={() => setUploadingTemplate(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Upload Document - {uploadingTemplate.name}
                </DialogTitle>
                <DialogDescription>
                  Upload a PDF or Word document template file
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
                  <p className="font-medium mb-1">Drop your file here or click to browse</p>
                  <p className="text-sm text-muted-foreground mb-4">Supports PDF, DOC, DOCX (max 10MB)</p>
                  <Input 
                    type="file" 
                    accept=".pdf,.doc,.docx" 
                    className="max-w-[200px] mx-auto"
                    data-testid="input-file-upload"
                  />
                </div>
                {uploadingTemplate.fileName && (
                  <div className="flex items-center gap-2 p-3 bg-muted rounded">
                    <FileText className="w-4 h-4" />
                    <span className="text-sm">Current file: {uploadingTemplate.fileName}</span>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setUploadingTemplate(null)} data-testid="button-cancel-upload">
                  Cancel
                </Button>
                <Button onClick={() => {
                  toast({ title: "Upload complete", description: "Document template has been uploaded" });
                  setUploadingTemplate(null);
                }} data-testid="button-confirm-upload">
                  Upload
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
    </>
  );

  if (inPanel) {
    return (
      <div className="w-full h-full flex flex-col overflow-auto p-4 sm:p-6">
        {innerContent}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        {innerContent}
      </DialogContent>
    </Dialog>
  );
}
